import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Clock,
  Play,
  CheckSquare,
  User,
  Calendar,
  Check,
  Trash2,
  MoreVertical,
  Edit2,
  CheckCircle,
  Circle,
  Send,
  List,
  Loader,
} from "react-feather";
import {
  useNavigate,
  useParams,
  useLocation,
  useOutletContext,
} from "react-router-dom";
import { Box } from "../../../components/Box";
import { DropdownMenu } from "../../../components/DropdownMenu";
import { Colors } from "../../../values/colors";
import { Text } from "../../../components/BaseComponents/styles";
import { LoadingOver } from "../../../components/LoadingOver";
import { toast } from "react-toastify";
import ActionPlansService from "../../../services/ActionPlans";
import { ActionPlanModal } from "../components/ActionPlanModal";
import { formatDateToDisplay } from "../../../utils/date";
import graduationIcon from "../../../assets/icons/graduation.svg";
import moment from "moment";
import {
  DetailBody,
  BackBtn,
  PracticedHeaderBtn,
  ProgressCard,
  StepperRow,
  StepBox,
  StepLine,
  StepIconWrap,
  StepLabel,
  SuccessBanner,
  BannerText,
  BannerSubtext,
  DesfazerBtn,
  InfoCard,
  CardTop,
  PriorityBadge,
  InfoCardTitle,
  InfoCardCategory,
  InfoCardDescription,
  InfoCardMeta,
  MenuItem,
  AcompSection,
  AcompTitle,
  ProgressRow,
  ProgressTrack,
  ProgressFill,
  ProgressLabel,
  TextareaBox,
  StyledTextarea,
  TextareaFooter,
  TextareaHint,
  SendBtn,
  ActivitiesList,
  ActivityItem,
  ActivityCheck,
  ActivityContent,
  ActivityText,
  ActivityEditInput,
  ActivityMeta,
  DeleteBtn,
  PracticedGroupHeader,
  PracticedGroupCount,
} from "./styles";
import { GraduationIcon } from "../styles";

const URGENCY_CONFIG = {
  critical: { label: "Crítica", color: Colors.red },
  high: { label: "Alta", color: Colors.orange },
  medium: { label: "Média", color: "#F0B429" },
};

const STEPS = [
  { label: "Pendentes", Icon: Clock },
  { label: "Em Andamento", Icon: Play },
  { label: "Concluída", Icon: CheckSquare },
];

const getStepVariant = (stepNum, currentStep) => {
  if (stepNum < currentStep) return "past";
  if (stepNum === currentStep) return "active";
  return "future";
};

const getCurrentStep = (plan) => {
  if (!plan) return 2;
  if (plan.action_completed || plan.status === "finalized") return 3;
  return 2;
};

const toastError = (error) =>
  toast.error(
    error?.response?.data?.message || error?.message || "Ocorreu um erro inesperado."
  );

const UserAvatar = ({ name }) => {
  const initial = name ? name.charAt(0).toUpperCase() : "U";
  return (
    <span
      style={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: Colors.primaryPurple,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 9,
        color: "#fff",
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initial}
    </span>
  );
};

const PlanCardMenu = ({ plan, onEdit, onDelete }) => (
  <>
    <Text size={13} weight={700} style={{ margin: "10px 14px 4px", display: "block" }}>
      Ações
    </Text>
    {!plan?.action_completed && (
      <MenuItem onClick={onEdit}>
        <Edit2 size={14} />
        Editar ação
      </MenuItem>
    )}
    <MenuItem variant="danger" onClick={onDelete}>
      <Trash2 size={14} />
      Deletar
    </MenuItem>
  </>
);

export const ActionPlanDetail = () => {
  const { id } = useParams();
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const [setOptions] = useOutletContext();

  const [plan, setPlan] = useState(locationState?.plan || null);
  const [activities, setActivities] = useState([]);
  const [activityText, setActivityText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [justPracticed, setJustPracticed] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const fetchPlan = useCallback(async () => {
    try {
      const data = await ActionPlansService.getById(id);
      setPlan(data);
    } catch (error) {
      toastError(error);
    }
  }, [id]);

  const fetchActivities = useCallback(async () => {
    try {
      const data = await ActionPlansService.listActivities(id);
      setActivities(data || []);
    } catch (error) {
      toastError(error);
    }
  }, [id]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchPlan(), fetchActivities()]);
      setLoading(false);
    };
    init();
  }, [id]);

  const handleTogglePracticed = useCallback(async () => {
    if (!plan) return;
    const wasCompleted = plan.action_completed;
    try {
      await ActionPlansService.toggleCompleted(id);
      setJustPracticed(!wasCompleted);
      await fetchPlan();
    } catch (error) {
      toastError(error);
    }
  }, [id, plan, fetchPlan]);

  const handleSendActivity = async () => {
    if (!activityText.trim()) return;
    setSending(true);
    try {
      await ActionPlansService.createActivity(id, { description: activityText.trim() });
      setActivityText("");
      await fetchActivities();
    } catch (error) {
      toastError(error);
    } finally {
      setSending(false);
    }
  };

  const handleMarkPracticed = async (activity) => {
    setTogglingId(activity.id);
    try {
      await ActionPlansService.updateActivity(id, activity.id, {
        description: activity.description,
        status: !activity.status,
      });
      await fetchActivities();
    } catch (error) {
      toastError(error);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    setDeletingId(activityId);
    try {
      await ActionPlansService.deleteActivity(id, activityId);
      await fetchActivities();
    } catch (error) {
      toastError(error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleStartEdit = (activity) => {
    setEditingActivityId(activity.id);
    setEditingText(activity.description);
  };

  const handleCancelEdit = () => {
    setEditingActivityId(null);
    setEditingText("");
  };

  const handleSaveActivityEdit = async (activity) => {
    const trimmed = editingText.trim();
    if (!trimmed || trimmed === activity.description) {
      handleCancelEdit();
      return;
    }
    try {
      await ActionPlansService.updateActivity(id, activity.id, {
        description: trimmed,
        status: activity.status,
      });
      await fetchActivities();
    } catch (error) {
      toastError(error);
    } finally {
      handleCancelEdit();
    }
  };

  const handleSaveEdit = async (updated) => {
    try {
      await ActionPlansService.update(id, updated);
      await fetchPlan();
      toast.success("Ação atualizada com sucesso.");
    } catch (error) {
      toastError(error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await ActionPlansService.remove(id);
      navigate(-1);
    } catch (error) {
      toastError(error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSendActivity();
    }
  };

  useEffect(() => {
    setOptions({
      start: (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <BackBtn onClick={() => navigate('/action-plan')}>
            <ArrowLeft size={18} color="#fff" />
          </BackBtn>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Text
              color="rgba(255,255,255,0.7)"
              size={11}
              weight={500}
              style={{ lineHeight: 1 }}
            >
              Plano de Ação
            </Text>
            <Text color="white" weight={700} style={{ lineHeight: 1 }}>
              {plan?.title || "—"}
            </Text>
          </div>
        </div>
      ),
      end: [
        <PracticedHeaderBtn
          key="practiced-btn"
          $active={plan?.action_completed}
          onClick={() =>
            plan?.action_completed
              ? handleTogglePracticed()
              : setModalMode("confirmPracticed")
          }
        >
          <Check size={14} />
          Praticada
        </PracticedHeaderBtn>,
      ],
    });
    return () => setOptions(null);
  }, [plan, navigate, setOptions, handleTogglePracticed]);

  const urgency = URGENCY_CONFIG[plan?.priority] || URGENCY_CONFIG.medium;
  const currentStep = getCurrentStep(plan);

  const pendingActivities = activities.filter((a) => !a.status);
  const practicedActivities = activities.filter((a) => !!a.status);
  const progressPct =
    activities.length > 0
      ? Math.round((practicedActivities.length / activities.length) * 100)
      : 0;

  if (loading) {
    return (
      <Box noPadding noHeader style={{ minHeight: "80vh", position: "relative" }}>
        <LoadingOver />
      </Box>
    );
  }

  return (
    <Box noPadding noHeader style={{ minHeight: "80vh" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <DetailBody>
        {/* ── Progress Stepper ── */}
        <ProgressCard>
          <Text weight={700} size={14} color={Colors.primaryColor}>Progresso</Text>
          <StepperRow>
            {STEPS.map((step, i) => {
              const stepNum = i + 1;
              const variant = getStepVariant(stepNum, currentStep);
              const isLast = i === STEPS.length - 1;
              return (
                <div
                  key={step.label}
                  style={{ display: "flex", alignItems: "center", flex: isLast ? 0 : 1 }}
                >
                  <StepBox $variant={variant}>
                    <StepIconWrap $variant={variant}>
                      <step.Icon size={14} />
                    </StepIconWrap>
                    <StepLabel $variant={variant}>{step.label}</StepLabel>
                  </StepBox>
                  {!isLast && <StepLine $solid={stepNum < currentStep} />}
                </div>
              );
            })}
          </StepperRow>

          {plan?.action_completed && justPracticed && (
            <SuccessBanner>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CheckCircle size={18} color={Colors.secondaryGreen} />
                <div>
                  <BannerText>Ação marcada como praticada</BannerText>
                  <BannerSubtext>
                    Registrada no acompanhamento como efetivamente realizada
                  </BannerSubtext>
                </div>
              </div>
              <DesfazerBtn onClick={() => setJustPracticed(false)}>Desfazer</DesfazerBtn>
            </SuccessBanner>
          )}
        </ProgressCard>

        {/* ── Plan Info Card ── */}
        {plan && (
          <>
          <InfoCard>
            <CardTop>
              <PriorityBadge bg={urgency.color}>{urgency.label}</PriorityBadge>
            </CardTop>

            <CardTop>
              <div style={{ display: "flex", alignItems: "center" }}>
                <GraduationIcon>
                  <img src={graduationIcon} alt="PGR - Plano de Ação" />
                </GraduationIcon>
                <div style={{ marginLeft: 10 }}>
                  <InfoCardTitle>{plan.title}</InfoCardTitle>
                  <InfoCardCategory>{plan.action_category?.name ?? "—"}</InfoCardCategory>
                </div>
              </div>
              <DropdownMenu icon={<MoreVertical size={18} />}>
                <PlanCardMenu
                  plan={plan}
                  onEdit={() => setModalMode("edit")}
                  onDelete={() => setModalMode("confirmDelete")}
                />
              </DropdownMenu>
            </CardTop>

            <InfoCardDescription>{plan.description}</InfoCardDescription>

            <InfoCardMeta>
              <User size={12} />
              <span>{plan.user?.name ?? "—"}</span>
              {plan.due_date && (
                <>
                  <Calendar size={12} style={{ marginLeft: 4 }} />
                  <span>{formatDateToDisplay(plan.due_date, "DD MMM YY")}</span>
                </>
              )}
            </InfoCardMeta>
          </InfoCard>
          </>
        )}

        {/* ── Acompanhamento ── */}
        <AcompSection>
          <AcompTitle>Acompanhamento</AcompTitle>

          <ProgressRow>
            <List size={18} color={Colors.secondaryPurple}/>
            <ProgressTrack>
              <ProgressFill style={{ width: `${progressPct}%` }} />
            </ProgressTrack>
            <ProgressLabel>
              {practicedActivities.length}/{activities.length} etapas ({progressPct}%)
            </ProgressLabel>
          </ProgressRow>

          <TextareaBox>
            <StyledTextarea
              placeholder="Registre uma atualização, observação ou resultados..."
              value={activityText}
              onChange={(e) => setActivityText(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={255}
              disabled={plan?.action_completed}
            />
            <TextareaFooter>
              <TextareaHint>Ctrl + Enter para enviar</TextareaHint>
              <SendBtn
                onClick={handleSendActivity}
                disabled={!activityText.trim() || sending || plan?.action_completed}
              >
                {sending ? (
                  <Loader size={13} style={{ animation: "spin 0.8s linear infinite" }} />
                ) : (
                  <Send size={13} />
                )}
                {sending ? "Enviando..." : "Enviar"}
              </SendBtn>
            </TextareaFooter>
          </TextareaBox>

          {pendingActivities.length > 0 && (
            <ActivitiesList>
              {pendingActivities.map((activity) => {
                const isToggling = togglingId === activity.id;
                const isDeleting = deletingId === activity.id;
                const isBusy = isToggling || isDeleting;
                return (
                  <ActivityItem key={activity.id} style={{ opacity: isBusy ? 0.6 : 1, pointerEvents: isBusy ? "none" : undefined }}>
                    <ActivityCheck onClick={() => handleMarkPracticed(activity)}>
                      {isToggling
                        ? <Loader size={18} style={{ animation: "spin 0.8s linear infinite" }} />
                        : <Circle size={18} color={Colors.ultraLightGray} />
                      }
                    </ActivityCheck>
                    <ActivityContent>
                      {editingActivityId === activity.id ? (
                        <ActivityEditInput
                          autoFocus
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          onBlur={() => handleSaveActivityEdit(activity)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveActivityEdit(activity);
                            if (e.key === "Escape") handleCancelEdit();
                          }}
                        />
                      ) : (
                        <ActivityText onClick={() => !plan?.action_completed && handleStartEdit(activity)}>
                          {activity.description}
                        </ActivityText>
                      )}
                      <ActivityMeta>
                        <UserAvatar name={activity.user?.name} />
                        <span>{activity.user?.name ?? "—"}</span>
                        <span>•</span>
                        <span>
                          {moment(activity.created_at).format("DD/MM/YYYY [às] HH[h]mm")}
                        </span>
                      </ActivityMeta>
                    </ActivityContent>
                    <DeleteBtn onClick={() => handleDeleteActivity(activity.id)}>
                      {isDeleting
                        ? <Loader size={14} style={{ animation: "spin 0.8s linear infinite" }} />
                        : <Trash2 size={14} />
                      }
                    </DeleteBtn>
                  </ActivityItem>
                );
              })}
            </ActivitiesList>
          )}

          {practicedActivities.length > 0 && (
            <>
              <PracticedGroupHeader>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: Colors.secondaryGreen,
                      display: "inline-block",
                    }}
                  />
                  Praticadas
                </span>
                <PracticedGroupCount>{practicedActivities.length}</PracticedGroupCount>
              </PracticedGroupHeader>

              <ActivitiesList>
                {practicedActivities.map((activity) => {
                  const isToggling = togglingId === activity.id;
                  const isDeleting = deletingId === activity.id;
                  const isBusy = isToggling || isDeleting;
                  return (
                    <ActivityItem key={activity.id} $practiced style={{ opacity: isBusy ? 0.6 : 1, pointerEvents: isBusy ? "none" : undefined }}>
                      <ActivityCheck $practiced onClick={() => handleMarkPracticed(activity)}>
                        {isToggling
                          ? <Loader size={18} style={{ animation: "spin 0.8s linear infinite" }} />
                          : <CheckCircle size={18} color={Colors.secondaryGreen} />
                        }
                      </ActivityCheck>
                      <ActivityContent>
                        <ActivityText $practiced>{activity.description}</ActivityText>
                        <ActivityMeta>
                          <UserAvatar name={activity.user?.name} />
                          <span>{activity.user?.name ?? "—"}</span>
                          <span>•</span>
                          <span>
                            {moment(activity.created_at).format("DD/MM/YYYY [às] HH[h]mm")}
                          </span>
                        </ActivityMeta>
                      </ActivityContent>
                      <DeleteBtn onClick={() => handleDeleteActivity(activity.id)}>
                        {isDeleting
                          ? <Loader size={14} style={{ animation: "spin 0.8s linear infinite" }} />
                          : <Trash2 size={14} />
                        }
                      </DeleteBtn>
                    </ActivityItem>
                  );
                })}
              </ActivitiesList>
            </>
          )}

          {activities.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "24px 0",
                color: Colors.lightGray,
                fontSize: 13,
              }}
            >
              Nenhuma atualização registrada ainda.
            </div>
          )}
        </AcompSection>
      </DetailBody>

      <ActionPlanModal
        open={modalMode === "edit" || modalMode === "confirmDelete" || modalMode === "confirmPracticed"}
        mode={modalMode}
        plan={plan}
        onClose={() => setModalMode(null)}
        onSave={handleSaveEdit}
        onDelete={handleConfirmDelete}
        onConfirmPracticed={handleTogglePracticed}
      />
    </Box>
  );
};
